import { CheckSquare, Square, Vote } from "@tamagui/lucide-icons";
import { action, computed, makeObservable } from "mobx";
import { TKey } from "../../../shared/translations/translationKeys";
import { ModuleType } from "../../../templates/types";
import { ActivityEventType } from "../../activities/types";
import type { Api } from "../../core/apis";
import type { Stores } from "../../core/stores";
import BaseModuleModel, {
  type MenuOption,
  type ModuleModelData,
  type Snapshot,
} from "../core/BaseModuleModel";
import { getDefaultSettingsForModule } from "../utils";
import PollOptionModel from "./data/PollOptionModel";
import PollVoteModel from "./data/PollVoteModel";
import {
  PollModuleBlockUI,
  PollModuleSetupUI,
  PollModuleStaticUI,
  PollModuleUI,
} from "./PollModuleUI";

export type PollModuleSnapshot = {
  numVoted: number;
  numNotVoted: number;
  selectedOptions: string[];
};

export type PollModuleSettings = {
  multipleVotes: boolean;
  showDescription: boolean;
};

export interface PollModuleModelData
  extends ModuleModelData<PollModuleSnapshot> {
  settings: PollModuleSettings;
  text: string;
  selectedOptions: string[];
}

export default class PollModuleModel extends BaseModuleModel<
  PollModuleSnapshot,
  PollModuleModelData
> {
  constructor(stores: Stores, api: Api, data: PollModuleModelData) {
    super(stores, api, data);

    Object.assign(this.data, {
      settings: getDefaultSettingsForModule(ModuleType.Poll, data.settings),
      text: data.text ?? "",
      selectedOptions: data.selectedOptions ?? [],
    });

    makeObservable(this);
  }

  // Used to show setting
  hasHelperGuide = false;

  @action
  async setDone(selectedOptions: string[]) {
    this.doneFlowEnabled = false;
    this.data.selectedOptions = selectedOptions;
    this.updateSnapshotWithoutSave();
    await this.toggleDoneState();
  }

  @action async updateSnapshotWithoutSave() {
    const foundPollVotes: string[] = [];
    if (this.data.selectedOptions.length) {
      this.pollOptions.forEach((option) => {
        if (this.data.selectedOptions.includes(option.uid)) {
          foundPollVotes.push(option.data.option);
        }
      });
    }

    this.data.snapshot = {
      numVoted: this.pollVotes.filter((vItem) => vItem.data.votes.length > 0)
        .length,
      numNotVoted: this.waitingForUsers.length,
      selectedOptions: foundPollVotes,
    };
  }

  @action async updateSettings(
    settings: Partial<PollModuleSettings>,
    skipSave?: boolean,
  ) {
    this.data.settings = { ...this.data.settings, ...settings };

    if (!skipSave) {
      await this.api.modulesApi.set(this);
      this.sendTelemetryEvent("settings_updated");
    }
  }

  @action setText = (text: string) => {
    if (this.data.text !== text) {
      this.updateTimestampWithoutSave();
      const oldValue = this.data.text ?? "";
      this.data.text = text;
      this.api.modulesApi.set(this);

      this.postActivityEvent(
        ActivityEventType.Edited,
        TKey.ActivityUpdatedTextInPoll,
        {
          oldValue,
          newValue: this.data.text ?? "",
        },
      );

      this.sendTelemetryEvent("text_updated");
    }
  };

  @computed get pollOptions() {
    return (
      this.dataItems.filter((item): item is PollOptionModel => {
        return PollOptionModel.is(item);
      }) ?? []
    );
  }

  @computed get pollOptionsSorted() {
    return this.pollOptions
      .slice()
      .sort((a, b) =>
        a.usersVotedArray.length > b.usersVotedArray.length ? -1 : 1,
      );
  }

  @computed get pollVotes() {
    return (
      this.dataItems.filter((item): item is PollVoteModel => {
        return PollVoteModel.is(item);
      }) ?? []
    );
  }

  @computed get hasVoted() {
    const votes = this.pollVotes.find((v) => v.data.userId === this.userUid);

    if (!votes) {
      return false;
    }

    return this.pollOptions.some((option) => {
      return votes.data.votes.includes(option.uid);
    });
  }

  @computed get waitingForUsers() {
    if (this.isDone) {
      return [];
    }

    return this.plan.users
      .filter(
        (user) =>
          !this.dataItems.some((item) => {
            return (
              PollVoteModel.is(item) &&
              item.data.userId === user.uid &&
              item.data.votes.length
            );
          }),
      )
      .map((user) => user.data.displayName);
  }

  render() {
    return <PollModuleUI module={this} />;
  }

  renderStatic() {
    return <PollModuleStaticUI module={this} />;
  }

  renderBlock() {
    return <PollModuleBlockUI module={this} />;
  }

  renderStaticBlock() {
    return <PollModuleBlockUI module={this} />;
  }

  renderIcon(size?: number) {
    return <Vote size={size} />;
  }

  renderMenuOptions(): MenuOption[] {
    return [
      {
        icon: this.data.settings.showDescription ? CheckSquare : Square,
        label: "Show description",
        onPress: () => {
          this.updateSettings({
            showDescription: !this.data.settings.showDescription,
          });
        },
      },
      {
        icon: this.data.settings.multipleVotes ? CheckSquare : Square,
        label: "Multiple selections",
        onPress: () => {
          this.updateSettings({
            multipleVotes: !this.data.settings.multipleVotes,
          });
        },
      },
    ];
  }

  renderBlockSetup = () => {
    return <PollModuleSetupUI module={this} />;
  };

  static is(
    module: BaseModuleModel<Snapshot, ModuleModelData<Snapshot>>,
  ): module is PollModuleModel {
    return module instanceof PollModuleModel;
  }
}
